import { useState } from 'react';
import type { PairMetric } from '../web3/usePairMetrics';
import type { NavView } from './Header';
import TokenIcon from './TokenIcon';
import Sparkline from './Sparkline';

type SortKey = 'tvl' | 'volume' | 'change';

const FEE_RATE = 0.003;

function compactUsd(v: number | undefined) {
  if (v === undefined) return '—';
  return `$${Intl.NumberFormat('en-US', { notation: 'compact', maximumFractionDigits: 2 }).format(v)}`;
}

function sortValue(m: PairMetric, key: SortKey): number {
  if (key === 'tvl') return m.tvlUsd ?? -1;
  if (key === 'volume') return m.volume24hUsd;
  return m.change24h;
}

// The professional-DeFi "Top Pools" surface: TVL / 24h Volume / Fees / 24h change,
// sorted, with a per-pool price sparkline. Mirrors PancakeSwap/Uniswap info tables.
export default function TopPoolsTable({
  metrics,
  loading,
  onNavigate,
}: {
  metrics: PairMetric[];
  loading: boolean;
  onNavigate: (view: NavView) => void;
}) {
  const [sort, setSort] = useState<SortKey>('tvl');

  const rows = [...metrics].sort((a, b) => sortValue(b, sort) - sortValue(a, sort));

  return (
    <div className="rounded-xl border border-abyss-700 bg-abyss-850 overflow-hidden">
      <div className="flex items-center justify-between px-4 py-3 border-b border-abyss-700">
        <h3 className="text-sm font-semibold text-foam-100">Top Pools</h3>
        <button
          onClick={() => onNavigate('Pools')}
          className="text-xs font-semibold text-tide-400 hover:text-tide-300"
        >
          전체 보기 →
        </button>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm min-w-[560px]">
          <thead>
            <tr className="text-[11px] uppercase tracking-wide text-foam-500">
              <th className="text-left font-medium px-4 py-2.5">Pool</th>
              <SortableTh label="TVL" active={sort === 'tvl'} onClick={() => setSort('tvl')} />
              <SortableTh label="24h Vol" active={sort === 'volume'} onClick={() => setSort('volume')} />
              <th className="text-right font-medium px-4 py-2.5">24h Fees</th>
              <SortableTh label="24h" active={sort === 'change'} onClick={() => setSort('change')} />
              <th className="text-right font-medium px-4 py-2.5">7d</th>
            </tr>
          </thead>
          <tbody>
            {loading
              ? [0, 1, 2, 3].map((i) => (
                  <tr key={i} className="border-t border-abyss-700">
                    <td colSpan={6} className="px-4 py-3">
                      <div className="h-5 rounded bg-abyss-700/60 animate-pulse" />
                    </td>
                  </tr>
                ))
              : rows.map((m) => <PoolRow key={m.pair.id} metric={m} onNavigate={onNavigate} />)}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function SortableTh({ label, active, onClick }: { label: string; active: boolean; onClick: () => void }) {
  return (
    <th className="text-right font-medium px-4 py-2.5">
      <button
        onClick={onClick}
        className={`inline-flex items-center gap-1 hover:text-foam-300 transition-colors ${
          active ? 'text-tide-400' : ''
        }`}
      >
        {label}
        {active && <span className="text-[8px]">▼</span>}
      </button>
    </th>
  );
}

function PoolRow({ metric, onNavigate }: { metric: PairMetric; onNavigate: (view: NavView) => void }) {
  const isUp = metric.change24h >= 0;
  const fees = metric.volume24hUsd * FEE_RATE;

  return (
    <tr
      onClick={() => onNavigate('Pools')}
      className="border-t border-abyss-700 hover:bg-abyss-800/50 cursor-pointer transition-colors"
    >
      <td className="px-4 py-3">
        <div className="flex items-center gap-2">
          <div className="flex -space-x-1.5">
            <TokenIcon symbol={metric.pair.base} size={22} />
            <TokenIcon symbol={metric.pair.quote} size={22} />
          </div>
          <span className="font-medium text-foam-100">
            {metric.pair.base}/{metric.pair.quote}
          </span>
        </div>
      </td>
      <td className="px-4 py-3 text-right tabular-nums text-foam-100">{compactUsd(metric.tvlUsd)}</td>
      <td className="px-4 py-3 text-right tabular-nums text-foam-300">
        {metric.volume24hUsd > 0 ? compactUsd(metric.volume24hUsd) : '—'}
      </td>
      <td className="px-4 py-3 text-right tabular-nums text-foam-300">
        {metric.volume24hUsd > 0 ? compactUsd(fees) : '—'}
      </td>
      <td className={`px-4 py-3 text-right tabular-nums font-medium ${isUp ? 'text-tide-400' : 'text-coral-400'}`}>
        {isUp ? '+' : ''}
        {metric.change24h.toFixed(2)}%
      </td>
      <td className="px-4 py-3">
        <div className="flex justify-end">
          <Sparkline
            data={metric.series.map((p) => p.price)}
            color={isUp ? '#22d3a8' : '#f5484f'}
            width={80}
            height={26}
          />
        </div>
      </td>
    </tr>
  );
}
